import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { fournisseurService } from "../../../../services/fournisseurService";

/**
 * Source de vérité de la page Fournisseurs : liste + opérations CRUD
 * (référentiel simple, peu nombreux → pas de pagination).
 */
const useFournisseurs = () => {
  const [fournisseurs, setFournisseurs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const cancelRef = useRef(false);

  const loadFournisseurs = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fournisseurService.getAllFournisseurs();
      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
      if (cancelRef.current) return list;
      setFournisseurs(list);
      return list;
    } catch (err) {
      toast.error("Erreur lors du chargement des fournisseurs.");
      return [];
    } finally {
      if (!cancelRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    cancelRef.current = false;
    loadFournisseurs();
    return () => {
      cancelRef.current = true;
    };
  }, [loadFournisseurs]);

  const creer = useCallback(async (nom, description, emails) => {
    try {
      const res = await fournisseurService.createFournisseur({
        nom,
        description,
        emails,
      });
      toast.success(`${nom} a été créé.`);
      return res.data?.uuid || null;
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Erreur lors de la création du fournisseur.",
      );
      return null;
    }
  }, []);

  const modifier = useCallback(async (uuid, nom, description, emails) => {
    try {
      await fournisseurService.updateFournisseur(uuid, {
        nom,
        description,
        emails,
      });
      toast.success("Fournisseur mis à jour.");
      return true;
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Erreur lors de la mise à jour du fournisseur.",
      );
      return false;
    }
  }, []);

  const supprimer = useCallback(async (uuid) => {
    try {
      await fournisseurService.deleteFournisseur(uuid);
      toast.success("Fournisseur supprimé.");
      return true;
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Ce fournisseur n'a pas pu être supprimé.",
      );
      return false;
    }
  }, []);

  return {
    fournisseurs,
    isLoading,
    loadFournisseurs,
    creer,
    modifier,
    supprimer,
  };
};

export default useFournisseurs;
